import SinglePropTracker from "../../../../prop-trackers/single-prop-tracker.js";
import PropTracker from "../../../../prop-trackers/prop-tracker.js";
import ValueTracker from "../../../../prop-trackers/value-tracker.js";
import { Mapper, ValueMapper } from "../../../../prop-tracker-utils/mapper.js";
import Zipper from "../../../../prop-tracker-utils/zipper.js";

class VisibilityTrackerController {
    constructor(evtCtx, shlWrapCtx, shlEvtCtx, visibilityController) {
        this._visibilityController = visibilityController;

        this._trModTracker = new PropTracker();
        this._trModMapper = new Mapper(this._trModTracker, (key) => new TransformModifierValueMapper(evtCtx));

        this._visFlagTracker = new PropTracker();
        this._visMapper = new Mapper(this._visFlagTracker, (key) => new VisibilityValueMapper(evtCtx));

        this._objectTracker = new PropTracker();

        this._zipper = new Zipper();
        this._zipper.setSource("transformModifier", this._trModMapper);
        this._zipper.setSource("visible", this._visMapper);
        this._zipper.setSource("object", this._objectTracker);
    }

    trackTransformModifierFlags(baseData, baseDataEvents) {
        this._trModTracker.track(baseData, baseDataEvents);
    }

    trackVisibilityFlags(slideData, slideDataEvents) {
        this._visFlagTracker.track(slideData, slideDataEvents);
    }

    trackObjects(objects, objectEvents) {
        this._objectTracker.track(objects, objectEvents);
    }

    update() {
        this._zipper.update(({ trackers }) => {
            for (const tracker of trackers) {
                tracker.update(({ userdata: objKey, props }) => {
                    const { newExists: trModExists, newValue: transformModifier } = props.get("transformModifier");
                    const { newExists: visExists, newValue: visible } = props.get("visible");
                    const { newExists: objExists, newValue: object } = props.get("object");

                    if (trModExists && visExists && objExists) {
                        this._visibilityController.setVisible(object, visible || transformModifier);
                    }
                });
            }
        });
    }
}

const TransformModifierProps = ["isHiddenRoot", "isMovableParent", "isMovableInverter"];

class TransformModifierValueMapper extends ValueMapper {
    constructor(evtCtx) {
        super();

        this._evtCtx = evtCtx;

        const needsUpdate = () => this.protected_needsUpdate();

        this._dataSet = new ValueTracker();
        this._dataSet.listenNeedsUpdate(needsUpdate);

        this._trackers = {};
        for (const prop of TransformModifierProps) {
            const tracker = new SinglePropTracker(prop);
            this._trackers[prop] = tracker;
            tracker.listenNeedsUpdate(needsUpdate);
        }

        this._valueTracker = new ValueTracker();
    }

    setValue(data) {
        this._dataSet.setValue(Boolean(data));

        const events = data ? this._evtCtx.getEvents(data) : undefined;

        for (const prop of TransformModifierProps) {
            this._trackers[prop].track(data, events, prop);
        }
    }

    deleteValue() {
        this._dataSet.setValue(false);

        for (const prop of TransformModifierProps) {
            this._trackers[prop].track();
        }
    }

    protected_startUpdate() {
        this._dataSet.update(({ prop: { newValue: dataSet } }) => {
            let isTransformModifier = false;

            for (const prop of TransformModifierProps) {
                this._trackers[prop].update(({ prop: { newValue } }) => {
                    isTransformModifier = isTransformModifier || newValue;
                });
            }

            if (dataSet) {
                this._valueTracker.setValue(Boolean(isTransformModifier));
            } else {
                this._valueTracker.deleteValue();
            }
        });

        return this._valueTracker.startUpdate();
    }

    protected_finishUpdate() {
        this._valueTracker.finishUpdate();
    }
}

class VisibilityValueMapper extends ValueMapper {
    constructor(evtCtx) {
        super();

        this._evtCtx = evtCtx;

        const needsUpdate = () => this.protected_needsUpdate();

        this._dataSet = new ValueTracker();
        this._dataSet.listenNeedsUpdate(needsUpdate);

        this._visibleTracker = new SinglePropTracker();
        this._deleteTracker = new SinglePropTracker();
        this._visibleTracker.listenNeedsUpdate(needsUpdate);
        this._deleteTracker.listenNeedsUpdate(needsUpdate);

        this._valueTracker = new ValueTracker();
    }

    setValue(data) {
        this._dataSet.setValue(Boolean(data));

        const events = data ? this._evtCtx.getEvents(data) : undefined;

        this._visibleTracker.track(data, events, "visible");
        this._deleteTracker.track(data, events, "deleted");
    }

    deleteValue() {
        this._dataSet.setValue(false);

        this._visibleTracker.track();
        this._deleteTracker.track();
    }

    protected_startUpdate() {
        this._dataSet.update(({ prop: { newValue: dataSet } }) => {
            this._visibleTracker.update(({ prop: { newExists: visibleExists, newValue: visibleValue } }) => {
                this._deleteTracker.update(({ prop: { newValue: deleted } }) => {
                    if (dataSet) {
                        const visible = !visibleExists || visibleValue;
                        this._valueTracker.setValue(visible && !deleted);
                    } else {
                        this._valueTracker.deleteValue();
                    }
                });
            });
        });

        return this._valueTracker.startUpdate();
    }

    protected_finishUpdate() {
        this._valueTracker.finishUpdate();
    }
}

export default VisibilityTrackerController;
